import "./tenderDetail.css";
import { ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";

import { splitGuideActionList, useGuideAction } from "../../guide/useGuideAction";
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronsUpDown,
  Download,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Star,
  Trash2,
  Users,
} from "lucide-react";

import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type { Column, ColumnDef } from "@tanstack/react-table";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import Section from "../../components/Section";
import TenderBoard from "./TenderBoard";
import TenderPlanUsagePanel from "./TenderPlanUsagePanel";
import LocationMap from "../../Map";
import { useModalContext } from "../../components/ModalProvider";
import { authenticationAtom } from "../../State";
import { userAPI } from "../../UserAPI";
import { parseRoles, ownRoleOptions } from "../../roleUtils";
import {
  useTenderSearchProfileAPI,
  TenderSearchProfile,
  TenderSearchProfileInput,
  Tender,
  TenderStateInput,
} from "../../TenderSearchProfileAPI";
import TenderExpressionBuilder from "./TenderExpressionBuilder";
import CpvCodePicker from "./CpvCodePicker";
import ExcludedTitleTermsInput from "./ExcludedTitleTermsInput";
import { exportElementAsPdf, pdfFilename } from "./tenderPdfExport";
import {
  formatPerformanceLocation,
  performanceLocationsCellText,
  performanceLocationsCsv,
  performanceLocationsEmptyText,
} from "./tenderPerformanceLocations";

type HelpTopic = "terms" | "cpv" | "exclusions";

// Number of bullet items (l1..lN) per help topic in the locale files.
const HELP_ITEM_COUNT: Record<HelpTopic, number> = { terms: 5, cpv: 4, exclusions: 3 };

interface Properties {
  refreshSignal: number;
}

const emptyProfile: TenderSearchProfileInput = {
  name: "",
  keywords: [],
  keywordExpressions: [],
  cpvCodes: [],
  country: "DEU",
  regions: null,
  roles: [],
  excludedTitleTerms: [],
};

// TED filters on ISO 3166-1 alpha-3 country codes (`CY = DEU`).
const COUNTRY_OPTIONS: { key: string; text: string }[] = [
  { key: "DEU", text: "Deutschland" },
  { key: "AUT", text: "Österreich" },
  { key: "CHE", text: "Schweiz" },
];

// TED matches `place-of-performance` against NUTS codes. Germany's NUTS-1
// codes are the 16 Bundesländer; region filtering only applies to Germany.
const BUNDESLAND_OPTIONS: { key: string; text: string }[] = [
  { key: "DE1", text: "Baden-Württemberg" },
  { key: "DE2", text: "Bayern" },
  { key: "DE3", text: "Berlin" },
  { key: "DE4", text: "Brandenburg" },
  { key: "DE5", text: "Bremen" },
  { key: "DE6", text: "Hamburg" },
  { key: "DE7", text: "Hessen" },
  { key: "DE8", text: "Mecklenburg-Vorpommern" },
  { key: "DE9", text: "Niedersachsen" },
  { key: "DEA", text: "Nordrhein-Westfalen" },
  { key: "DEB", text: "Rheinland-Pfalz" },
  { key: "DEC", text: "Saarland" },
  { key: "DED", text: "Sachsen" },
  { key: "DEE", text: "Sachsen-Anhalt" },
  { key: "DEF", text: "Schleswig-Holstein" },
  { key: "DEG", text: "Thüringen" },
];

// Legacy profiles may have stored 2-letter codes; normalize to alpha-3.
const COUNTRY_ALIASES: Record<string, string> = { DE: "DEU", AT: "AUT", CH: "CHE" };
const normalizeCountry = (code?: string | null): string =>
  code ? COUNTRY_ALIASES[code] ?? code : "DEU";

const countryLabel = (code?: string | null): string =>
  COUNTRY_OPTIONS.find((o) => o.key === normalizeCountry(code))?.text || code || "—";
const regionLabels = (codes?: string[] | null): string | null =>
  codes && codes.length
    ? codes.map((c) => BUNDESLAND_OPTIONS.find((o) => o.key === c)?.text || c).join(", ")
    : null;

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

/** Leerzustand einer Tabelle: Überschrift, Erklärung und Handlungsaufforderung. */
function TableEmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
      {action}
    </div>
  );
}

/**
 * Mehrfachauswahl als Popover mit Kontrollkästchen — ersetzt die
 * Fluent-Dropdown-Mehrfachauswahl. Der Auswahlzustand bleibt eine
 * Schlüsselliste, die Fachlogik dahinter ist unverändert.
 */
function MultiSelect(properties: {
  label: string;
  placeholder: string;
  emptyLabel: string;
  options: { key: string; text: string }[];
  selectedKeys: string[];
  onToggle: (key: string, selected: boolean) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const labelId = useId();

  const triggerText =
    properties.options
      .filter((option) => properties.selectedKeys.includes(option.key))
      .map((option) => option.text)
      .join(", ") || properties.placeholder;

  return (
    <div className={properties.className ?? "flex min-w-0 flex-1 flex-col gap-1.5"}>
      <Label id={labelId}>{properties.label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-labelledby={labelId}
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">{triggerText}</span>
            <ChevronsUpDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandList>
              <CommandEmpty>{properties.emptyLabel}</CommandEmpty>
              <CommandGroup>
                {properties.options.map((option) => {
                  const selected = properties.selectedKeys.includes(option.key);
                  return (
                    <CommandItem
                      key={option.key}
                      value={option.text}
                      className="gap-2"
                      onSelect={() => properties.onToggle(option.key, !selected)}
                    >
                      <Checkbox checked={selected} tabIndex={-1} className="pointer-events-none" />
                      <span className="min-w-0 break-words">{option.text}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// --- Localization helpers for TED multilingual fields ---

// Older bund.de tenders were stored with undecoded numeric/named HTML
// entities ("f&#252;r"); the server decodes new data at the source, this is
// the display fallback for existing records.
const NAMED_ENTITIES: Record<string, string> = {
  lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  auml: "ä", Auml: "Ä", ouml: "ö", Ouml: "Ö", uuml: "ü", Uuml: "Ü", szlig: "ß",
};

function decodeEntities(text: string): string {
  if (!text.includes("&")) return text;
  return text
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(/&([a-zA-Z]+);/g, (match, name) =>
      NAMED_ENTITIES[name] ?? (name === "amp" ? "&" : match)
    );
}

function localizedText(obj: any): string {
  if (obj == null) return "";
  if (typeof obj === "string") return decodeEntities(obj);
  if (Array.isArray(obj)) return decodeEntities(obj[0] ?? "");
  const val = obj.deu ?? obj.eng ?? obj.mul ?? Object.values(obj)[0];
  if (Array.isArray(val)) return decodeEntities(val[0] ?? "");
  return decodeEntities(val ?? "");
}

function localizedArray(obj: any): string[] {
  if (obj == null) return [];
  if (typeof obj === "string") return [decodeEntities(obj)];
  if (Array.isArray(obj)) return obj.map((item) => decodeEntities(String(item)));
  const val = obj.deu ?? obj.eng ?? obj.mul ?? Object.values(obj)[0];
  if (Array.isArray(val)) return val.map((item) => decodeEntities(String(item)));
  if (val) return [decodeEntities(String(val))];
  return [];
}

function formatDate(value: any): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatDateTime(value: any): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function uniqueArray(arr: string[]): string[] {
  return Array.from(new Set(arr));
}

// TED durations/deadlines may be strings or { value, unit } objects (possibly
// in arrays) — never render them directly as React children.
function formatPeriod(value: any): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(formatPeriod).filter(Boolean).join(", ");
  if (typeof value === "object" && "value" in value) {
    return `${formatPeriod(value.value)} ${formatPeriod(value.unit)}`.trim();
  }
  return "";
}

// --- Tender location map ---
//
// Shows the tender's structured addresses (buyer, place of performance) on
// the shared Leaflet Map component (the same one chat locations use).
// Deterministic only: addresses come straight from the tender fields and are
// geocoded via the existing Geoapify-backed endpoint — no LLM extraction.

const GEOCODE_COUNTRY: Record<string, string> = {
  DEU: "de",
  DE: "de",
  AUT: "at",
  AT: "at",
  CHE: "ch",
  CH: "ch",
};

interface TenderMapMarker {
  lat: number;
  lon: number;
  name: string;
  type: string;
}

/** Kopfzeile eines Abschnitts im Ausschreibungs-Detail (TED-Blau, bewusst beibehalten). */
function DetailSectionHeader({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`border-b border-[#dde] bg-[#eef1f7] px-3 py-2 text-sm font-semibold text-[#003399] ${className ?? ""}`}
    >
      {children}
    </div>
  );
}

function TenderLocationMap({ tender }: { tender: Tender }) {
  const [markers, setMarkers] = useState<TenderMapMarker[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const buyer = tender.buyer || {};
    const candidates = [
      {
        label: "Auftraggeber",
        street: buyer.street,
        postCode: buyer.postCode,
        city: buyer.city,
        country: buyer.country,
      },
      {
        label: "Erfüllungsort",
        street: tender.performanceStreet,
        postCode: tender.performancePostCode,
        city: tender.performanceCity,
        country: tender.performanceCountry || tender.country,
      },
    ]
      .map((candidate) => ({
        ...candidate,
        text: [
          decodeEntities(candidate.street || ""),
          [candidate.postCode, decodeEntities(candidate.city || "")].filter(Boolean).join(" "),
        ]
          .filter(Boolean)
          .join(", "),
      }))
      // A city is the minimum for a meaningful pin.
      .filter((candidate) => candidate.city);

    // Identical addresses collapse into one marker with a combined label.
    const unique: typeof candidates = [];
    for (const candidate of candidates) {
      const existing = unique.find((u) => u.text === candidate.text);
      if (existing) existing.label = `${existing.label} & ${candidate.label}`;
      else unique.push(candidate);
    }

    if (!unique.length) {
      setMarkers([]);
      return;
    }

    (async () => {
      const results: TenderMapMarker[] = [];
      for (const candidate of unique) {
        try {
          const suggestions = await userAPI.getGeocodeAutocomplete(
            candidate.text,
            1,
            GEOCODE_COUNTRY[String(candidate.country || "").toUpperCase()] || "de"
          );
          const hit = suggestions?.[0];
          if (hit && typeof hit.lat === "number" && typeof hit.lon === "number") {
            results.push({
              lat: hit.lat,
              lon: hit.lon,
              name: `${candidate.label}: ${candidate.text}`,
              type: "address",
            });
          }
        } catch {
          /* the map is best-effort — a failed geocode just omits the pin */
        }
      }
      if (!cancelled) setMarkers(results);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tender._id]);

  if (!markers || !markers.length) return null;

  const pad = 0.01;
  const lats = markers.map((m) => m.lat);
  const lons = markers.map((m) => m.lon);
  const bounds = [
    [Math.min(...lats) - pad, Math.min(...lons) - pad],
    [Math.max(...lats) + pad, Math.max(...lons) + pad],
  ];

  return (
    <div className="mb-4 min-w-0">
      <DetailSectionHeader>Karte</DetailSectionHeader>
      <div className="mt-2 min-w-0">
        <LocationMap query={null} height={300} hideOverlay pois={markers} options={{ bounds }} />
      </div>
    </div>
  );
}

// --- Tender Detail Modal ---

function TenderDetailModal({ tender, onDismiss }: { tender: Tender | null; onDismiss: () => void }) {
  const { t } = useTranslation();
  const api = useTenderSearchProfileAPI();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [crawled, setCrawled] = useState<{
    markdown?: string;
    url?: string;
    documents?: Tender["crawledDocuments"];
    // The analysis result as the single-tender endpoint has it. Fresher than the list row, so an
    // analysis that finished after the list was loaded shows without reloading the page.
    performanceLocations?: Tender["performanceLocations"];
    performanceLocationsUpdatedAt?: string | null;
  } | null>(null);

  // The crawled page markdown and extracted documents are projected out of the
  // list endpoint (for size), so fetch the full tender when the modal opens.
  useEffect(() => {
    setCrawled(null);
    const id = tender?._id;
    if (!id) return;
    const apply = (t: Tender | null | undefined) => {
      setCrawled({
        markdown: t?.crawledMarkdown,
        url: t?.crawledUrl,
        documents: t?.crawledDocuments,
        performanceLocations: t?.performanceLocations ?? null,
        performanceLocationsUpdatedAt: t?.performanceLocationsUpdatedAt ?? null,
      });
    };
    // Fast path if the caller already supplied the heavy fields; otherwise fetch.
    if (tender?.crawledMarkdown || tender?.crawledDocuments) {
      apply(tender);
      return;
    }
    let cancelled = false;
    api
      .getTender(id)
      .then((full) => {
        if (!cancelled) apply(full);
      })
      .catch(() => {
        // Best-effort: the detail view still shows without the crawled text.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tender?._id]);

  const exportPdf = async () => {
    if (!contentRef.current || !tender) return;
    setExportingPdf(true);
    try {
      await exportElementAsPdf(
        contentRef.current,
        pdfFilename(tender.publicationNumber || localizedText(tender.title) || tender._id)
      );
    } catch (error) {
      console.error("PDF export failed:", error);
    } finally {
      setExportingPdf(false);
    }
  };

  if (!tender) return null;

  // Only successfully-extracted documents are shown; failed ones (download/parse
  // errors) stay in the DB for diagnosis but are hidden from the view.
  const okCrawledDocs = (crawled?.documents ?? []).filter(
    (doc) => doc.status === "ok" && doc.markdown
  );

  const title = localizedText(tender.title) || localizedText(tender.titleLot) || "—";
  const buyer = tender.buyer || {};
  const buyerName = localizedArray(buyer.name).join(", ") || "—";
  const buyerAddress = [buyer.street, [buyer.postCode, buyer.city].filter(Boolean).join(" "), buyer.country]
    .filter(Boolean)
    .join(", ");
  const descLot = localizedArray(tender.descriptionLot);
  const descProc = localizedArray(tender.descriptionProc);
  const additionalLot = localizedArray(tender.additionalInformationLot);
  const additionalProc = localizedArray(tender.additionalInfoProc);
  const selCriteria = localizedArray(tender.selectionCriteria);
  const cpvUnique = uniqueArray(tender.cpvCodes || []);
  const nutsUnique = uniqueArray(tender.placesOfPerformance || []);
  const contractNature = Array.isArray(tender.contractNature)
    ? uniqueArray(tender.contractNature).join(", ")
    : tender.contractNature || "";
  const dur = tender.contractDuration || {};
  // The analysis result: the freshly fetched tender if it has arrived, otherwise the list row.
  const analysed: Pick<Tender, "performanceLocations"> = crawled
    ? { performanceLocations: crawled.performanceLocations }
    : tender;
  const analysedLocations = analysed.performanceLocations || [];
  const analysedEmptyText = performanceLocationsEmptyText(analysed);
  const analysedAt = crawled
    ? crawled.performanceLocationsUpdatedAt
    : tender.performanceLocationsUpdatedAt;
  const tedUrl = tender.publicationNumber
    ? `https://ted.europa.eu/de/notice/-/detail/${tender.publicationNumber}`
    : null;

  const Row = ({ label, children }: { label: string; children?: React.ReactNode }) => {
    if (!children) return null;
    return (
      <tr>
        <th
          scope="row"
          className="w-[200px] whitespace-nowrap px-3 py-2 text-left align-top font-medium text-muted-foreground"
        >
          {label}
        </th>
        <td className="min-w-0 break-words px-3 py-2 align-top">{children}</td>
      </tr>
    );
  };

  const DetailSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
    const filtered = (Array.isArray(children) ? children : [children]).filter(Boolean);
    if (!filtered.length) return null;
    return (
      <div className="mb-4 min-w-0">
        <DetailSectionHeader>{title}</DetailSectionHeader>
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full border-collapse">
            <tbody>{filtered}</tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onDismiss(); }}>
      <DialogContent className="w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle className="min-w-0 break-words">{title}</DialogTitle>
          {tender.publicationNumber ? (
            <DialogDescription>
              {`Bekanntmachungsnummer: ${tender.publicationNumber}`}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <div ref={contentRef} id="tender-pdf-content" className="max-h-[65vh] min-w-0 overflow-y-auto">
          {tedUrl && (
            <div className="mb-3">
              <a
                href={tedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#003399] underline underline-offset-2 hover:no-underline"
              >
                Auf TED ansehen ↗
              </a>
            </div>
          )}

          <DetailSection title="Auftraggeber">
            <Row label="Name">{buyerName !== "—" ? buyerName : undefined}</Row>
            <Row label="Adresse">{buyerAddress || undefined}</Row>
            <Row label="Kontaktstelle">{buyer.contactPoint || undefined}</Row>
            <Row label="E-Mail">
              {buyer.email ? (
                <a className="underline underline-offset-2 hover:no-underline" href={`mailto:${buyer.email}`}>
                  {buyer.email}
                </a>
              ) : undefined}
            </Row>
            <Row label="Internet">
              {buyer.internetAddress ? (
                <a
                  className="break-all underline underline-offset-2 hover:no-underline"
                  href={buyer.internetAddress}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {buyer.internetAddress}
                </a>
              ) : undefined}
            </Row>
            <Row label="Kennung">{buyer.identifier || undefined}</Row>
            <Row label="Rechtsform">{buyer.legalType || undefined}</Row>
            <Row label="Haupttätigkeit">{tender.mainActivity || undefined}</Row>
          </DetailSection>

          <DetailSection title="Auftragsgegenstand">
            {descLot.length > 0 && (
              <Row label="Beschreibung">
                <div className="whitespace-pre-wrap">{descLot.join("\n\n")}</div>
              </Row>
            )}
            {descProc.length > 0 && (
              <Row label="Verfahrensbeschreibung">
                <div className="whitespace-pre-wrap">{descProc.join("\n\n")}</div>
              </Row>
            )}
            <Row label="Los-Titel">{localizedText(tender.titleLot) || undefined}</Row>
            <Row label="CPV-Codes">{cpvUnique.length ? cpvUnique.join(", ") : undefined}</Row>
            <Row label="Art des Auftrags">{contractNature || undefined}</Row>
            <Row label="Los-Kennung">{tender.lotIdentifier || undefined}</Row>
            <Row label="Interne Kennung">{tender.referenceNumber || undefined}</Row>
            {tender.estimatedValue != null && (
              <Row label="Geschätzter Wert">
                {new Intl.NumberFormat("de-DE", {
                  style: "currency",
                  currency: tender.estimatedValueCurrency || "EUR",
                }).format(tender.estimatedValue)}
              </Row>
            )}
            {(dur.startDate || dur.endDate) && (
              <Row label="Laufzeit">{formatDate(dur.startDate)} — {formatDate(dur.endDate)}</Row>
            )}
            {dur.period && !dur.startDate && !dur.endDate && (
              <Row label="Laufzeit">{formatPeriod(dur.period)}</Row>
            )}
            {additionalLot.length > 0 && (
              <Row label="Zusätzliche Angaben">
                <div className="whitespace-pre-wrap">{additionalLot.join("\n\n")}</div>
              </Row>
            )}
            {additionalProc.length > 0 && (
              <Row label="Zusätzliche Angaben (Verfahren)">
                <div className="whitespace-pre-wrap">{additionalProc.join("\n\n")}</div>
              </Row>
            )}
          </DetailSection>

          <DetailSection title="Erfüllungsort">
            <Row label="Adresse">
              {[tender.performanceStreet, [tender.performancePostCode, tender.performanceCity].filter(Boolean).join(" "), tender.performanceCountry]
                .filter(Boolean)
                .join(", ") || undefined}
            </Row>
            <Row label="NUTS-Code">{nutsUnique.length ? nutsUnique.join(", ") : undefined}</Row>
          </DetailSection>

          {/* The places the detailed analysis found in the Vergabeunterlagen — usually several, and
              not the same thing as the single Erfüllungsort the notice itself publishes above. */}
          <DetailSection title="Ausführungsorte (Detailanalyse)">
            {analysedEmptyText ? (
              <Row label="Ermittelte Orte">
                <span className="text-muted-foreground">{analysedEmptyText}</span>
              </Row>
            ) : (
              <Row label={`Ermittelte Orte (${analysedLocations.length})`}>
                <ol className="m-0 list-decimal pl-5">
                  {analysedLocations.map((location, i) => (
                    <li key={i} className="mb-0.5">
                      {formatPerformanceLocation(location)}
                    </li>
                  ))}
                </ol>
              </Row>
            )}
            {analysedAt ? <Row label="Stand der Analyse">{formatDateTime(analysedAt)}</Row> : false}
          </DetailSection>

          <TenderLocationMap tender={tender} />

          <DetailSection title="Verfahren">
            <Row label="Verfahrensart">{tender.procedureType || undefined}</Row>
            <Row label="Bekanntmachungstyp">{tender.noticeType || undefined}</Row>
            <Row label="Formtyp">{tender.formType || undefined}</Row>
            <Row label="Rechtsgrundlage">{tender.legalBasis || undefined}</Row>
            <Row label="Verfahrens-ID">{tender.procedureIdentifier || undefined}</Row>
            <Row label="Rahmenvereinbarung">{tender.frameworkAgreement || undefined}</Row>
          </DetailSection>

          <DetailSection title="Termine">
            <Row label="Veröffentlichungsdatum">{tender.publicationDate ? formatDate(tender.publicationDate) : undefined}</Row>
            <Row label="Absendedatum">{tender.dispatchDate ? formatDate(tender.dispatchDate) : undefined}</Row>
            <Row label="Angebotsfrist">{tender.deadline ? formatDateTime(tender.deadline) : undefined}</Row>
          </DetailSection>

          {tender.awardCriteria && tender.awardCriteria.length > 0 && (
            <DetailSection title="Zuschlagskriterien">
              {tender.awardCriteria.map((crit, i) => (
                <Row key={i} label="Kriterium">
                  {[crit.name, crit.type].filter(Boolean).join(" — ")}
                  {crit.weight ? ` (${crit.weight}%)` : ""}
                </Row>
              ))}
            </DetailSection>
          )}

          {selCriteria.length > 0 && (
            <DetailSection title="Eignungskriterien">
              <Row label="Kriterien">
                <div className="whitespace-pre-wrap">{selCriteria.join("\n\n")}</div>
              </Row>
            </DetailSection>
          )}

          <DetailSection title="Einreichung">
            <Row label="Sprache">
              {tender.submissionLanguage?.length ? tender.submissionLanguage.join(", ") : undefined}
            </Row>
            <Row label="Elektronische Einreichung">{tender.electronicSubmission || undefined}</Row>
            <Row label="Varianten/Alternativangebote">{tender.variantAllowed || undefined}</Row>
            <Row label="KMU-geeignet">
              {tender.suitableForSme === true ? "Ja" : tender.suitableForSme === false ? "Nein" : undefined}
            </Row>
            {tender.tenderValidityDeadline && (
              <Row label="Bindefrist">{formatPeriod(tender.tenderValidityDeadline)}</Row>
            )}
            <Row label="Auftragsunterlagen">
              {tender.documents?.urls?.length ? (
                <div>
                  {tender.documents.urls.map((url, i) => (
                    <div key={i}>
                      <a
                        className="break-all underline underline-offset-2 hover:no-underline"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {url}
                      </a>
                    </div>
                  ))}
                </div>
              ) : undefined}
            </Row>
            <Row label="Einreichungs-URL">
              {tender.documents?.submissionUrls?.length ? (
                <div>
                  {tender.documents.submissionUrls.map((url, i) => (
                    <div key={i}>
                      <a
                        className="break-all underline underline-offset-2 hover:no-underline"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {url}
                      </a>
                    </div>
                  ))}
                </div>
              ) : undefined}
            </Row>
          </DetailSection>

          {crawled?.markdown && (
            <div className="mb-4 min-w-0">
              <DetailSectionHeader>Ausschreibungstext (Originalseite)</DetailSectionHeader>
              <details className="tender-collapsible mt-1.5">
                <summary>Originalseite anzeigen</summary>
                <div className="tender-collapsible-body">
                  {crawled.url && (
                    <div className="mb-2">
                      <a
                        href={crawled.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-[#003399] underline underline-offset-2 hover:no-underline"
                      >
                        {crawled.url} ↗
                      </a>
                    </div>
                  )}
                  <div className="tender-md">
                    <Markdown remarkPlugins={[remarkGfm]}>{crawled.markdown}</Markdown>
                  </div>
                </div>
              </details>
            </div>
          )}

          {okCrawledDocs.length > 0 && (
            <div className="mb-4 min-w-0">
              <DetailSectionHeader className="mb-1.5">
                Vergabeunterlagen (extrahiert) · {okCrawledDocs.length}
              </DetailSectionHeader>
              {okCrawledDocs.map((doc, i) => (
                <details key={i} className="tender-collapsible">
                  <summary>
                    <span className="break-words">{doc.fileName || doc.url}</span>
                  </summary>
                  <div className="tender-collapsible-body tender-md">
                    <Markdown remarkPlugins={[remarkGfm]}>{doc.markdown}</Markdown>
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="gap-2 max-sm:w-full"
            disabled={exportingPdf}
            title={t("tenderSearchProfiles.tenders.exportPdf")}
            onClick={exportPdf}
          >
            {exportingPdf ? (
              <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download aria-hidden className="h-3.5 w-3.5" />
            )}
            PDF
          </Button>
          <Button variant="outline" className="max-sm:w-full" onClick={onDismiss}>
            {t("global.close") || "Schließen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Main Panel ---

export default function TenderSearchProfilesPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const api = useTenderSearchProfileAPI();
  const [authentication] = useAtom(authenticationAtom) as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const fieldId = useId();

  const [profiles, setProfiles] = useState<TenderSearchProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [editedProfile, setEditedProfile] = useState<TenderSearchProfileInput | null>(null);
  const [editedProfileId, setEditedProfileId] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [availableRoles, setAvailableRoles] = useState<{ key: string; text: string }[]>([]);
  const [searchingIds, setSearchingIds] = useState<Set<string>>(new Set());

  // AI assistant modal (opened from the edit modal)
  const [aiOpen, setAiOpen] = useState(false);
  const [suggestDescription, setSuggestDescription] = useState("");
  const [suggesting, setSuggesting] = useState(false);

  // Field explanation modal
  const [helpTopic, setHelpTopic] = useState<HelpTopic | null>(null);

  // Tenders view (all profiles) with search/filter state.
  // The tab lives in the address (?tab), not in local state: otherwise „Suchprofile“ would not be
  // linkable, and the portal guide would have to announce the tab switch as a click instruction
  // instead of taking the user there directly (CONTRACT.md §7.3, this panel's registry route).
  const activeTab = searchParams.get("tab") || "board";
  const setActiveTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    // replace: the tab switch is not a separate step in the user's history.
    setSearchParams(next, { replace: true });
  };
  const [allTenders, setAllTenders] = useState<Tender[]>([]);
  const [tendersLoading, setTendersLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  // "mine" (default) scopes to profiles matching the user's roles; "all" and
  // specific profile ids are always available — roles are a default filter,
  // not an access restriction.
  const [filterProfileId, setFilterProfileId] = useState<string>("mine");
  const [statusFilter, setStatusFilter] = useState<string>("active");

  const searchFieldId = `${fieldId}-search`;
  const profileFilterId = `${fieldId}-profile-filter`;
  const statusFilterId = `${fieldId}-status-filter`;
  const nameFieldId = `${fieldId}-name`;
  const countryFieldId = `${fieldId}-country`;
  const suggestFieldId = `${fieldId}-suggest`;

  // "New" markers: tenders created since the previous visit (per browser).
  const lastSeenKey = `tenderLastSeen:${authentication?.customer || ""}`;
  const [lastSeen] = useState<number>(() => {
    const raw = localStorage.getItem(lastSeenKey);
    return raw ? parseInt(raw, 10) : 0;
  });
  useEffect(() => {
    localStorage.setItem(lastSeenKey, String(Date.now()));
  }, [lastSeenKey]);
  const isNewTender = (tender: Tender): boolean =>
    lastSeen > 0 && !!tender.createdAt && new Date(tender.createdAt).getTime() > lastSeen;

  // Tender detail modal
  const [selectedTender, setSelectedTender] = useState<Tender | null>(null);

  const loadAllTenders = async () => {
    setTendersLoading(true);
    try {
      const result = await api.listTenders();
      setAllTenders(result || []);
    } catch (error) {
      pushError(error);
    } finally {
      setTendersLoading(false);
    }
  };

  const triggerSearch = async (id: string) => {
    setSearchingIds((prev) => new Set(prev).add(id));
    try {
      const result = await api.search(id);
      loadAllTenders();
      return result;
    } catch (error) {
      pushError(error);
    } finally {
      setSearchingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Team-wide favorite/archive flags.
  const setTenderFlag = async (tender: Tender, patch: TenderStateInput) => {
    try {
      const state = await api.setTenderState(tender._id, patch);
      setAllTenders((prev) =>
        prev.map((item) =>
          item._id === tender._id
            ? { ...item, favorite: state.favorite, archived: state.archived }
            : item
        )
      );
    } catch (error) {
      pushError(error);
    }
  };

  const showProfileTenders = (profile: TenderSearchProfile) => {
    setFilterProfileId(profile._id);
    setActiveTab("tenders");
  };

  useEffect(() => {
    if (!authentication?.customer) return;
    let cancelled = false;
    userAPI.getRoles(authentication.customer)
      .then((data) => {
        if (cancelled) return;
        // Only the customer's own roles ("Eigene Rollen" in the Teamverwaltung). Systemrollen are
        // platform permissions and Legacy roles belong to another tenant — neither is a department,
        // so neither belongs in a search profile, the Bereich filter or a Weitergabe.
        setAvailableRoles(ownRoleOptions(parseRoles(data), authentication.customer));
      })
      .catch((e) => console.error("Error loading roles:", e));
    return () => { cancelled = true; };
  }, [authentication?.customer]);

  const load = async () => {
    try {
      const result = await api.list();
      setProfiles(result || []);
    } catch (error) {
      pushError(error);
    } finally {
      setProfilesLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadAllTenders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  const profileNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const profile of profiles) map.set(profile._id, profile.name);
    return map;
  }, [profiles]);

  // Role ids assigned to the signed-in user (JWT roleAssignments carry the
  // populated role or a bare role id).
  const myRoleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const assignment of authentication?.roleAssignments || []) {
      const role = assignment?.role;
      if (typeof role === "string") ids.add(role);
      else if (role?._id) ids.add(String(role._id));
    }
    return ids;
  }, [authentication]);

  // A profile counts as "mine" when it has no role restriction or shares a
  // role with the user.
  const myProfileIds = useMemo(() => {
    const ids = new Set<string>();
    for (const profile of profiles) {
      const roles = profile.roles || [];
      if (!roles.length || roles.some((id) => myRoleIds.has(id))) ids.add(profile._id);
    }
    return ids;
  }, [profiles, myRoleIds]);

  const inProfileScope = useCallback(
    (tender: Tender): boolean => {
      if (filterProfileId === "all") return true;
      const matched = tender.matchedProfiles || [];
      if (filterProfileId === "mine") return matched.some((m) => myProfileIds.has(m.profile));
      return matched.some((m) => m.profile === filterProfileId);
    },
    [filterProfileId, myProfileIds]
  );

  const matchCountByProfile = useMemo(() => {
    const counts = new Map<string, number>();
    for (const tender of allTenders) {
      for (const match of tender.matchedProfiles || []) {
        counts.set(match.profile, (counts.get(match.profile) || 0) + 1);
      }
    }
    return counts;
  }, [allTenders]);

  // Counts are embedded into the row data (not read via a column closure) so
  // the data table re-renders when the tenders finish loading.
  const profileRows = useMemo(
    () =>
      profiles.map((profile) => ({
        ...profile,
        matchCount: matchCountByProfile.get(profile._id) || 0,
      })),
    [profiles, matchCountByProfile]
  );

  const filteredTenders = useMemo(() => {
    // Every whitespace-separated word must occur somewhere in the tender.
    const words = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return allTenders.filter((tender) => {
      if (!inProfileScope(tender)) return false;
      if (statusFilter === "active" && tender.archived) return false;
      if (statusFilter === "favorites" && !tender.favorite) return false;
      if (statusFilter === "archived" && !tender.archived) return false;
      if (words.length) {
        const haystack = [
          localizedText(tender.title),
          localizedText(tender.titleLot),
          localizedArray(tender.buyer?.name).join(" "),
          tender.buyer?.city,
          tender.performanceCity,
          (tender.cpvCodes || []).join(" "),
          tender.publicationNumber,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!words.every((word) => haystack.includes(word))) return false;
      }
      return true;
    });
  }, [allTenders, inProfileScope, statusFilter, searchText]);

  // CSV of the currently filtered list — semicolon-separated with BOM so
  // German Excel opens it correctly.
  const exportCsv = () => {
    const header = [
      "Titel",
      "Auftraggeber",
      "Veröffentlicht",
      "Angebotsfrist",
      "Ort",
      "Ausführungsorte",
      "CPV-Codes",
      "Profile",
      "Favorit",
      "Archiviert",
      "Link",
    ];
    const rows = filteredTenders.map((tender) => [
      localizedText(tender.title) || localizedText(tender.titleLot) || "",
      localizedArray(tender.buyer?.name).join(", "),
      formatDate(tender.publicationDate),
      formatDateTime(tender.deadline),
      [tender.buyer?.city || tender.performanceCity, tender.country || tender.performanceCountry]
        .filter(Boolean)
        .join(", "),
      performanceLocationsCsv(tender),
      uniqueArray(tender.cpvCodes || []).join(", "),
      (tender.matchedProfiles || [])
        .map((match) => profileNameById.get(match.profile) || "")
        .filter(Boolean)
        .join(", "),
      tender.favorite ? "Ja" : "Nein",
      tender.archived ? "Ja" : "Nein",
      (tender as any).sourceUrl ||
        (tender.publicationNumber
          ? `https://ted.europa.eu/de/notice/-/detail/${tender.publicationNumber}`
          : ""),
    ]);
    const escapeField = (value: string) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv =
      "\ufeff" +
      [header, ...rows].map((row) => row.map(escapeField).join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "ausschreibungen.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const columns: ColumnDef<any>[] = [
    {
      id: "name",
      accessorFn: (row: TenderSearchProfile) => row.name,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("tenderSearchProfiles.name")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <span className="block max-w-[180px] break-words text-sm">{row.original.name}</span>
      ),
    },
    {
      id: "matches",
      accessorFn: (row: TenderSearchProfile & { matchCount?: number }) => row.matchCount ?? 0,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("tenderSearchProfiles.matches")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">{row.original.matchCount ?? 0}</span>
      ),
    },
    {
      id: "searchTerms",
      accessorFn: (row: TenderSearchProfile) =>
        [...(row.keywords || []), ...(row.keywordExpressions || [])].join(", "),
      header: () => <span className="font-bold text-foreground">{t("tenderSearchProfiles.searchTerms")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[260px] break-words text-sm">
          {[...(row.original.keywords || []), ...(row.original.keywordExpressions || [])].join(", ")}
          {(row.original.excludedTitleTerms || []).length > 0 && (
            <span className="mt-0.5 block text-xs text-destructive">
              {t("tenderSearchProfiles.exclusions.listSummary", {
                terms: (row.original.excludedTitleTerms || []).join(", "),
              })}
            </span>
          )}
        </span>
      ),
    },
    {
      id: "cpvCodes",
      accessorFn: (row: TenderSearchProfile) => (row.cpvCodes || []).join(", "),
      header: () => <span className="font-bold text-foreground">{t("tenderSearchProfiles.cpvCodes")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[130px] break-words text-sm">
          {(row.original.cpvCodes || []).join(", ")}
        </span>
      ),
    },
    {
      id: "country",
      accessorFn: (row: TenderSearchProfile) => countryLabel(row.country),
      header: () => <span className="font-bold text-foreground">{t("tenderSearchProfiles.country")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[110px] break-words text-sm">{countryLabel(row.original.country)}</span>
      ),
    },
    {
      id: "regions",
      accessorFn: (row: TenderSearchProfile) =>
        regionLabels(row.regions) ?? t("tenderSearchProfiles.allRegions"),
      header: () => <span className="font-bold text-foreground">{t("tenderSearchProfiles.regions")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[150px] break-words text-sm">
          {regionLabels(row.original.regions) ?? t("tenderSearchProfiles.allRegions")}
        </span>
      ),
    },
    {
      id: "roles",
      accessorFn: (row: TenderSearchProfile) =>
        (row.roles || [])
          .map((id) => availableRoles.find((r) => r.key === id)?.text || id)
          .join(", "),
      header: () => <span className="font-bold text-foreground">{t("tenderSearchProfiles.roles")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[150px] break-words text-sm">
          {(row.original.roles || [])
            .map((id: string) => availableRoles.find((r) => r.key === id)?.text || id)
            .join(", ")}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">{t("tenderSearchProfiles.edit")}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const profile = row.original;
        const isSearching = searchingIds.has(profile._id);

        return (
          <div className="flex flex-wrap items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("tenderSearchProfiles.edit")}
              title={t("tenderSearchProfiles.edit")}
              onClick={(event) => {
                event.stopPropagation();
                openEdit(profile);
              }}
            >
              <Pencil aria-hidden className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={isSearching}
              aria-label={t("tenderSearchProfiles.search")}
              title={t("tenderSearchProfiles.search")}
              onClick={(event) => {
                event.stopPropagation();
                triggerSearch(profile._id);
              }}
            >
              {isSearching
                ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                : <Search aria-hidden className="h-4 w-4" />}
            </Button>
          </div>
        );
      },
    },
  ];

  // Deadlines: soon-expiring (≤ 7 days) highlighted, expired greyed out.
  const deadlineCell = (row: Tender) => {
    const text = formatDateTime(row.deadline);
    if (!row.deadline || text === "—") return <span className="text-sm">{text}</span>;
    const time = new Date(row.deadline).getTime();
    if (isNaN(time)) return <span className="text-sm">{text}</span>;
    const days = (time - Date.now()) / 86400000;
    if (days < 0) return <span className="text-sm text-muted-foreground">{text}</span>;
    if (days <= 7) return <span className="text-sm font-semibold text-destructive">{text}</span>;
    return <span className="text-sm">{text}</span>;
  };

  // Tender list columns (all-profiles view)
  const tenderColumns: ColumnDef<any>[] = [
    {
      id: "favorite",
      header: () => <span className="sr-only">{t("tenderSearchProfiles.tenders.favorite")}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const tender = row.original;

        return (
          <Button
            variant="ghost"
            size="icon"
            aria-label={
              tender.favorite
                ? t("tenderSearchProfiles.tenders.unfavorite")
                : t("tenderSearchProfiles.tenders.favorite")
            }
            title={
              tender.favorite
                ? t("tenderSearchProfiles.tenders.unfavorite")
                : t("tenderSearchProfiles.tenders.favorite")
            }
            onClick={(event) => {
              event.stopPropagation();
              setTenderFlag(tender, { favorite: !tender.favorite });
            }}
          >
            {/* Goldene Füllung wie im Bestand, Kontur im warning-Token: der
                Stern selbst erreicht damit die 3:1 für nicht-textliche
                Elemente (die reine Füllung #eab308 liegt bei 1,9:1). */}
            <Star
              aria-hidden
              className={tender.favorite ? "h-4 w-4 fill-[#eab308] text-warning" : "h-4 w-4 text-muted-foreground"}
            />
          </Button>
        );
      },
    },
    {
      id: "title",
      accessorFn: (row: Tender) => localizedText(row.title) || localizedText(row.titleLot) || "—",
      header: ({ column }) => <SortableHeader column={column}>Titel</SortableHeader>,
      cell: ({ row }) => (
        <div className="flex min-w-0 max-w-[280px] items-center gap-1.5">
          {isNewTender(row.original) && (
            <Badge className="shrink-0 px-1.5 py-0 text-[10px]">
              {t("tenderSearchProfiles.tenders.new")}
            </Badge>
          )}
          <span className="min-w-0 truncate text-sm">
            {localizedText(row.original.title) || localizedText(row.original.titleLot) || "—"}
          </span>
        </div>
      ),
    },
    {
      id: "profile",
      accessorFn: (row: Tender) =>
        uniqueArray(
          (row.matchedProfiles || [])
            .map((match) => profileNameById.get(match.profile) || "")
            .filter(Boolean)
        ).join(", "),
      header: () => (
        <span className="font-bold text-foreground">{t("tenderSearchProfiles.tenders.profileColumn")}</span>
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[130px] break-words text-sm">
          {uniqueArray(
            (row.original.matchedProfiles || [])
              .map((match: any) => profileNameById.get(match.profile) || "")
              .filter(Boolean)
          ).join(", ")}
        </span>
      ),
    },
    {
      id: "buyer",
      accessorFn: (row: Tender) => localizedArray(row.buyer?.name).join(", ") || "—",
      header: () => <span className="font-bold text-foreground">Auftraggeber</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[180px] break-words text-sm">
          {localizedArray(row.original.buyer?.name).join(", ") || "—"}
        </span>
      ),
    },
    {
      id: "publicationDate",
      accessorFn: (row: Tender) => (row.publicationDate ? new Date(row.publicationDate).getTime() : 0),
      header: ({ column }) => <SortableHeader column={column}>Veröffentlicht</SortableHeader>,
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm">{formatDate(row.original.publicationDate)}</span>
      ),
    },
    {
      id: "deadline",
      accessorFn: (row: Tender) => (row.deadline ? new Date(row.deadline).getTime() : 0),
      header: ({ column }) => <SortableHeader column={column}>Angebotsfrist</SortableHeader>,
      cell: ({ row }) => <span className="whitespace-nowrap">{deadlineCell(row.original)}</span>,
    },
    {
      id: "location",
      accessorFn: (row: Tender) => {
        const city = row.buyer?.city || row.performanceCity;
        const country = row.country || row.performanceCountry;
        return [city, country].filter(Boolean).join(", ") || "—";
      },
      header: () => <span className="font-bold text-foreground">Ort</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const city = row.original.buyer?.city || row.original.performanceCity;
        const country = row.original.country || row.original.performanceCountry;

        return (
          <span className="block max-w-[120px] break-words text-sm">
            {[city, country].filter(Boolean).join(", ") || "—"}
          </span>
        );
      },
    },
    {
      // The analysed places — one plus a count, with the full list in the detail modal. "—" means
      // no analysis has run; "Keine" means one has and found nothing.
      id: "performanceLocations",
      accessorFn: (row: Tender) => performanceLocationsCellText(row),
      header: () => <span className="font-bold text-foreground">Ausführungsorte</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className="block max-w-[150px] truncate text-sm"
          title={(row.original.performanceLocations || []).map(formatPerformanceLocation).join("\n")}
        >
          {performanceLocationsCellText(row.original)}
        </span>
      ),
    },
    {
      id: "archive",
      header: () => <span className="sr-only">{t("tenderSearchProfiles.tenders.archive")}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const tender = row.original;

        return (
          <Button
            variant="ghost"
            size="icon"
            aria-label={
              tender.archived
                ? t("tenderSearchProfiles.tenders.unarchive")
                : t("tenderSearchProfiles.tenders.archive")
            }
            title={
              tender.archived
                ? t("tenderSearchProfiles.tenders.unarchive")
                : t("tenderSearchProfiles.tenders.archive")
            }
            onClick={(event) => {
              event.stopPropagation();
              setTenderFlag(tender, { archived: !tender.archived });
            }}
          >
            {tender.archived
              ? <ArchiveRestore aria-hidden className="h-4 w-4" />
              : <Archive aria-hidden className="h-4 w-4 text-muted-foreground" />}
          </Button>
        );
      },
    },
  ];

  const openCreate = () => {
    setIsNew(true);
    setEditedProfileId(null);
    setSuggestDescription("");
    setEditedProfile({ ...emptyProfile });
  };

  /**
   * Prefill from the address
   * (`?panel=tenderSearchProfiles&tab=profiles&guide=settings.tenderSearchProfiles.create&name=…&keywords=a,b`):
   * the same as `openCreate`, just with name and keywords from the address. The keywords are a
   * list (`guideAction.listFields`) and arrive as a comma-separated list — an address carries
   * exactly one value per field. CPV codes, country and regions remain selection fields.
   */
  useGuideAction(
    "settings.tenderSearchProfiles.create",
    (values) => {
      setIsNew(true);
      setEditedProfileId(null);
      setSuggestDescription("");
      setEditedProfile({
        ...emptyProfile,
        name: values.name ?? "",
        keywords: splitGuideActionList(values.keywords),
      });
    },
    { panel: "tenderSearchProfiles", tab: "profiles" },
  );

  const openEdit = (profile: TenderSearchProfile) => {
    setIsNew(false);
    setSuggestDescription("");
    setEditedProfileId(profile._id);
    setEditedProfile({
      name: profile.name,
      keywords: profile.keywords || [],
      keywordExpressions: profile.keywordExpressions || [],
      cpvCodes: profile.cpvCodes || [],
      country: normalizeCountry(profile.country),
      regions: profile.regions ?? null,
      roles: profile.roles || [],
      excludedTitleTerms: profile.excludedTitleTerms || [],
    });
  };

  const saveProfile = async () => {
    if (!editedProfile) return;

    setSaving(true);
    try {
      if (isNew) {
        await api.create(editedProfile);
      } else if (editedProfileId) {
        await api.update(editedProfileId, editedProfile);
      }
      setEditedProfile(null);
      await load();
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;

    try {
      await api.remove(deleteConfirmId);
      if (filterProfileId === deleteConfirmId) setFilterProfileId("mine");
      setDeleteConfirmId(null);
      await load();
      loadAllTenders();
    } catch (error) {
      pushError(error);
    }
  };

  // AI assistant: description → suggested keywords/expressions/CPV codes,
  // merged (deduplicated) into the profile being edited.
  const runSuggest = async () => {
    if (!editedProfile || !suggestDescription.trim()) return;
    setSuggesting(true);
    try {
      const suggestion = await api.suggest(suggestDescription.trim());
      setEditedProfile({
        ...editedProfile,
        keywords: uniqueArray([...(editedProfile.keywords || []), ...suggestion.keywords]),
        keywordExpressions: uniqueArray([
          ...(editedProfile.keywordExpressions || []),
          ...suggestion.keywordExpressions,
        ]),
        cpvCodes: uniqueArray([...(editedProfile.cpvCodes || []), ...suggestion.cpvCodes]),
        excludedTitleTerms: uniqueArray([
          ...(editedProfile.excludedTitleTerms || []),
          ...(suggestion.excludedTitleTerms || []),
        ]),
      });
      setAiOpen(false);
    } catch (error) {
      pushError(error);
    } finally {
      setSuggesting(false);
    }
  };

  const tenderFilterActive =
    searchText.trim().length > 0 || statusFilter !== "active" || filterProfileId !== "mine";

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <Section title={t("tenderSearchProfiles.title")}>
        {/* Above the tabs, so the plan is in view on the board and next to „Suchprofil anlegen".
            Renders nothing for a customer without a plan. Mounted once the profiles are in, so the
            profile count in its key does not fetch twice on open. */}
        {!profilesLoading && (
          <TenderPlanUsagePanel reloadKey={`${properties.refreshSignal}:${profiles.length}`} />
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            {/* The board is the landing tab: it is the screen the work actually happens on, while
                the flat list below is the browse/search/CSV surface. */}
            <TabsTrigger value="board">{t("tenderBoard.tab")}</TabsTrigger>
            {/* Order and names as in the board wireframe (19.10.2026): Board, Trefferliste, Suchprofile. */}
            <TabsTrigger value="tenders">
              {t("tenderSearchProfiles.tendersTab")}
              <span className="text-xs text-muted-foreground">
                ({allTenders
                  .filter((tender) => !tender.archived && inProfileScope(tender))
                  .length.toLocaleString("de-DE")})
              </span>
              {tendersLoading && <Loader2 aria-hidden size={12} className="animate-spin" />}
            </TabsTrigger>
            <TabsTrigger value="profiles">
              {t("tenderSearchProfiles.profilesTab")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="board">
            <TenderBoard
              roles={availableRoles}
              tenderRoleIds={Array.from(
                new Set(profiles.flatMap((profile) => profile.roles || []))
              )}
              refreshSignal={properties.refreshSignal}
            />
          </TabsContent>

          <TabsContent value="profiles">
            <div className="flex min-w-0 flex-col gap-4">
              <div className="flex flex-wrap">
                <Button data-guide-id="settings.tenderSearchProfiles.create" variant="outline" className="gap-2 max-sm:w-full" onClick={openCreate}>
                  <Plus aria-hidden className="h-4 w-4" />
                  {t("tenderSearchProfiles.create")}
                </Button>
              </div>
              <div className="min-w-0 overflow-x-auto">
                <DataTable
                  columns={columns}
                  data={profileRows}
                  loading={profilesLoading}
                  onRowClick={(profile: any) => showProfileTenders(profile)}
                  previousAriaLabel={t("global.back")}
                  nextAriaLabel={t("global.next")}
                  emptyMessage={
                    <TableEmptyState
                      title={t("tenderSearchProfiles.noProfiles")}
                      hint={t("tenderSearchProfiles.noProfilesHint")}
                      action={
                        <Button data-guide-id="settings.tenderSearchProfiles.create" variant="outline" size="sm" className="gap-2" onClick={openCreate}>
                          <Plus aria-hidden className="h-4 w-4" />
                          {t("tenderSearchProfiles.create")}
                        </Button>
                      }
                    />
                  }
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tenders">
            <div className="flex min-w-0 flex-col gap-4">
              <div className="flex min-w-0 flex-wrap items-end gap-4">
                <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
                  <Label htmlFor={searchFieldId}>{t("tenderSearchProfiles.tenders.searchLabel")}</Label>
                  <Input
                    id={searchFieldId}
                    placeholder={t("tenderSearchProfiles.tenders.searchPlaceholder")}
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={profileFilterId}>{t("tenderSearchProfiles.tenders.profileColumn")}</Label>
                  <Select
                    value={filterProfileId}
                    onValueChange={(value: string) => setFilterProfileId(value)}
                  >
                    <SelectTrigger id={profileFilterId} className="w-[200px] max-w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)]">
                      <SelectItem value="mine">{t("tenderSearchProfiles.tenders.myProfiles")}</SelectItem>
                      <SelectItem value="all">{t("tenderSearchProfiles.tenders.allProfiles")}</SelectItem>
                      {profiles.map((profile) => (
                        <SelectItem key={profile._id} value={profile._id} className="whitespace-normal">
                          {profile.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={statusFilterId}>{t("tenderSearchProfiles.tenders.statusLabel")}</Label>
                  <Select value={statusFilter} onValueChange={(value: string) => setStatusFilter(value)}>
                    <SelectTrigger id={statusFilterId} className="w-[150px] max-w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("tenderSearchProfiles.tenders.statusActive")}</SelectItem>
                      <SelectItem value="favorites">{t("tenderSearchProfiles.tenders.statusFavorites")}</SelectItem>
                      <SelectItem value="archived">{t("tenderSearchProfiles.tenders.statusArchived")}</SelectItem>
                      <SelectItem value="all">{t("tenderSearchProfiles.tenders.statusAll")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  className="gap-2 max-sm:w-full"
                  disabled={!filteredTenders.length}
                  title={t("tenderSearchProfiles.tenders.exportCsv")}
                  onClick={exportCsv}
                >
                  <Download aria-hidden className="h-3.5 w-3.5" />
                  CSV
                </Button>
              </div>
              <div className="min-w-0 overflow-x-auto">
                <DataTable
                  columns={tenderColumns}
                  data={filteredTenders}
                  loading={tendersLoading}
                  pageSize={15}
                  onRowClick={(tender: any) => setSelectedTender(tender)}
                  previousAriaLabel={t("global.back")}
                  nextAriaLabel={t("global.next")}
                  emptyMessage={
                    tenderFilterActive ? (
                      <TableEmptyState
                        title={t("tenderSearchProfiles.tenders.noMatches")}
                        hint={t("tenderSearchProfiles.tenders.noMatchesHint")}
                        action={
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSearchText("");
                              setStatusFilter("active");
                              setFilterProfileId("mine");
                            }}
                          >
                            {t("billingPanel.resetFilter")}
                          </Button>
                        }
                      />
                    ) : (
                      <TableEmptyState
                        title={t("tenderSearchProfiles.tenders.noTenders")}
                        hint={t("tenderSearchProfiles.tenders.noTendersHint")}
                      />
                    )
                  }
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </Section>

      {/* Tender detail modal */}
      {selectedTender && (
        <TenderDetailModal
          tender={selectedTender}
          onDismiss={() => setSelectedTender(null)}
        />
      )}

      {/* Create / Edit dialog */}
      <Dialog open={!!editedProfile} onOpenChange={(open) => { if (!open) setEditedProfile(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("tenderSearchProfiles.create") : t("tenderSearchProfiles.edit")}
            </DialogTitle>
          </DialogHeader>
          {editedProfile ? (
            <div className="flex max-h-[65vh] min-w-0 flex-col gap-5 overflow-y-auto p-0.5">
              <div className="flex min-w-0 flex-wrap items-end gap-2">
                <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
                  <Label htmlFor={nameFieldId}>{t("tenderSearchProfiles.name")}</Label>
                  <Input
                    id={nameFieldId}
                    value={editedProfile.name}
                    onChange={(event) =>
                      setEditedProfile({ ...editedProfile, name: event.target.value || "" })
                    }
                  />
                </div>
                <Button
                  variant="outline"
                  className="gap-2"
                  title={t("tenderSearchProfiles.assistant.description")}
                  onClick={() => setAiOpen(true)}
                >
                  <Sparkles aria-hidden className="h-3.5 w-3.5" />
                  {t("tenderSearchProfiles.assistant.button")}
                </Button>
              </div>
              <TenderExpressionBuilder
                keywords={editedProfile.keywords || []}
                expressions={editedProfile.keywordExpressions || []}
                onChange={({ keywords, keywordExpressions }) =>
                  setEditedProfile({ ...editedProfile, keywords, keywordExpressions })
                }
                onHelp={() => setHelpTopic("terms")}
              />
              <CpvCodePicker
                value={editedProfile.cpvCodes || []}
                onChange={(cpvCodes) => setEditedProfile({ ...editedProfile, cpvCodes })}
                onHelp={() => setHelpTopic("cpv")}
              />
              <ExcludedTitleTermsInput
                value={editedProfile.excludedTitleTerms || []}
                onChange={(excludedTitleTerms) => setEditedProfile({ ...editedProfile, excludedTitleTerms })}
                onHelp={() => setHelpTopic("exclusions")}
              />
              <div className="flex min-w-0 flex-wrap gap-4">
                <div className="flex w-[180px] max-w-full flex-col gap-1.5">
                  <Label htmlFor={countryFieldId}>{t("tenderSearchProfiles.country")}</Label>
                  <Select
                    value={editedProfile.country || "DEU"}
                    onValueChange={(country: string) => {
                      setEditedProfile({
                        ...editedProfile,
                        country,
                        // Regions are German NUTS codes — drop them for other countries.
                        regions: country === "DEU" ? editedProfile.regions : null,
                      });
                    }}
                  >
                    <SelectTrigger id={countryFieldId}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map((option) => (
                        <SelectItem key={option.key} value={option.key}>{option.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {editedProfile.country === "DEU" ? (
                  <MultiSelect
                    label={t("tenderSearchProfiles.regions")}
                    placeholder={t("tenderSearchProfiles.allRegionsPlaceholder")}
                    emptyLabel={t("global.noInformationAvailable")}
                    options={BUNDESLAND_OPTIONS}
                    selectedKeys={editedProfile.regions || []}
                    onToggle={(key, selected) => {
                      const current = editedProfile.regions || [];
                      const next = selected
                        ? [...current, key]
                        : current.filter((r) => r !== key);
                      setEditedProfile({
                        ...editedProfile,
                        regions: next.length ? next : null,
                      });
                    }}
                  />
                ) : null}
              </div>
              <div className="flex min-w-0 flex-col gap-1">
                <MultiSelect
                  label={t("tenderSearchProfiles.roles")}
                  placeholder={t("global.pleaseSelect")}
                  emptyLabel={t("global.noInformationAvailable")}
                  options={availableRoles}
                  selectedKeys={editedProfile.roles || []}
                  className="flex min-w-0 flex-col gap-1.5"
                  onToggle={(key, selected) => {
                    const current = editedProfile.roles || [];
                    const next = selected
                      ? [...current, key]
                      : current.filter((r) => r !== key);
                    setEditedProfile({ ...editedProfile, roles: next });
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {t("tenderSearchProfiles.rolesHint")}{" "}
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-foreground underline underline-offset-2 hover:opacity-70"
                    onClick={() => {
                      // Leave the editor and jump to team management, where
                      // roles are created and assigned to users.
                      setEditedProfile(null);
                      setSearchParams({ panel: "teamManagement" });
                    }}
                  >
                    <Users aria-hidden size={12} />
                    {t("tenderSearchProfiles.manageRoles")}
                  </button>
                </p>
              </div>
              {!isNew && editedProfileId ? (
                <div className="flex flex-wrap">
                  <Button
                    variant="ghost"
                    className="gap-2 text-destructive hover:text-destructive max-sm:w-full"
                    onClick={() => {
                      setEditedProfile(null);
                      setDeleteConfirmId(editedProfileId);
                    }}
                  >
                    <Trash2 aria-hidden className="h-4 w-4" />
                    {t("tenderSearchProfiles.delete")}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="max-sm:w-full" onClick={() => setEditedProfile(null)}>
              {t("global.cancel")}
            </Button>
            <Button
              className="max-sm:w-full"
              disabled={!editedProfile?.name || saving}
              onClick={saveProfile}
            >
              {saving ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("global.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tenderSearchProfiles.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tenderSearchProfiles.confirmDeleteText")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 max-sm:w-full"
              onClick={confirmDelete}
            >
              {t("tenderSearchProfiles.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI assistant dialog */}
      <Dialog
        open={aiOpen}
        onOpenChange={(open) => {
          if (!open && !suggesting) setAiOpen(false);
        }}
      >
        <DialogContent
          className="w-[calc(100vw-2rem)] sm:max-w-[560px]"
          // Während der Assistent läuft, schließt ein Klick daneben nicht —
          // sonst ginge das Ergebnis verloren.
          onInteractOutside={(event) => { if (suggesting) event.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>{t("tenderSearchProfiles.assistant.title")}</DialogTitle>
            <DialogDescription>{t("tenderSearchProfiles.assistant.description")}</DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={suggestFieldId} className="sr-only">
              {t("tenderSearchProfiles.assistant.description")}
            </Label>
            <Textarea
              id={suggestFieldId}
              rows={4}
              autoFocus
              className="min-h-24"
              placeholder={t("tenderSearchProfiles.assistant.placeholder")}
              value={suggestDescription}
              onChange={(event) => setSuggestDescription(event.target.value)}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="max-sm:w-full"
              disabled={suggesting}
              onClick={() => setAiOpen(false)}
            >
              {t("global.cancel")}
            </Button>
            <Button
              className="gap-2 max-sm:w-full"
              disabled={suggesting || !suggestDescription.trim()}
              onClick={runSuggest}
            >
              {suggesting ? (
                <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles aria-hidden className="h-3.5 w-3.5" />
              )}
              {t("tenderSearchProfiles.assistant.generate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field explanation dialog */}
      <Dialog open={!!helpTopic} onOpenChange={(open) => { if (!open) setHelpTopic(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{helpTopic ? t(`tenderSearchProfiles.help.${helpTopic}.title`) : ""}</DialogTitle>
          </DialogHeader>
          {helpTopic ? (
            <div className="flex flex-col gap-3 text-sm">
              <p>{t(`tenderSearchProfiles.help.${helpTopic}.intro`)}</p>
              <ul className="flex list-disc flex-col gap-1.5 pl-5">
                {Array.from({ length: HELP_ITEM_COUNT[helpTopic] }, (_, i) => `l${i + 1}`).map(
                  (key) => (
                    <li key={key}>{t(`tenderSearchProfiles.help.${helpTopic}.${key}`)}</li>
                  )
                )}
              </ul>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" className="max-sm:w-full" onClick={() => setHelpTopic(null)}>
              {t("global.close") || "Schließen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
